/**
 * Focus helpers shared by the dialog shell (`ModalShellComponent`: its Tab
 * trap and focus return), the work pane's focus rules (`WorkPaneA11y`) and
 * the basemap key dialog. Plain DOM, no Angular; for focusing an element that
 * only exists after the next render, see `focus-after-render.ts` beside it.
 */

/**
 * Elements that can be Tab stops. `tabbablesIn` filters the matches further
 * (a negative `tabindex`, hidden or inert ancestors, `checkVisibility`).
 * Radio groups are not modelled (the browser stops once per group, this list
 * once per radio); no dialog has radios today.
 */
const TABBABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'details > summary:first-of-type',
  '[contenteditable]',
  '[tabindex]',
].join(', ')

/** Ancestors (or the element itself) that take an element out of the Tab order. */
const HIDDEN_OR_INERT = '[inert], [hidden], [aria-hidden="true"]'

/** An explicit negative `tabindex` (a roving list row, a `tabindex="-1"` button): never a Tab stop. */
function hasNegativeTabIndex(el: Element): boolean {
  const attr = el.getAttribute('tabindex')
  return attr !== null && attr.trim() !== '' && Number(attr) < 0
}

/**
 * Whether `el` is rendered. Uses `checkVisibility` where the browser has it
 * (also `visibility: hidden`); jsdom has no layout and no `checkVisibility`,
 * so there it is always true. No `getClientRects` / `offsetParent` test, for
 * the same reason.
 */
function isRendered(el: HTMLElement): boolean {
  return el.checkVisibility?.({ visibilityProperty: true }) ?? true
}

/**
 * A match that is no Tab stop after all: `[tabindex]` re-admits a disabled
 * control (`<button disabled tabindex="0">`, one inside `fieldset[disabled]`),
 * and `[contenteditable]` matches `contenteditable="false"` too.
 */
function isExcluded(el: HTMLElement): boolean {
  if (el.matches(':disabled')) return true
  return el.hasAttribute('contenteditable') && !el.hasAttribute('tabindex') && !isEditable(el)
}

/** `isContentEditable` where the engine has it (jsdom does not), else the attribute. */
function isEditable(el: HTMLElement): boolean {
  if (typeof el.isContentEditable === 'boolean') return el.isContentEditable
  const value = el.getAttribute('contenteditable')
  return value === '' || value === 'true' || value === 'plaintext-only'
}

/**
 * The Tab stops inside `root`, in document order. Query it fresh on every
 * Tab: a dialog's content changes while it is open (busy states, error lines).
 */
export function tabbablesIn(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE))
    .filter(el => !hasNegativeTabIndex(el) && !isExcluded(el) && el.closest(HIDDEN_OR_INERT) === null && isRendered(el))
    // Browsers return a selector list's matches in document order; jsdom's selector engine may not.
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
}

/** Whether `el` can still take focus: in the page, enabled, not inside `[inert]` or `[aria-hidden="true"]`. */
export function canTakeFocus(el: Element | null | undefined): el is HTMLElement {
  return el instanceof HTMLElement
    && el.isConnected
    && !el.matches(':disabled')
    && el.closest('[inert], [aria-hidden="true"]') === null
}

/** Focuses `el` without scrolling; whether it took focus (a disabled or removed element does not). */
export function refocus(el: HTMLElement | null | undefined): boolean {
  if (!el?.isConnected) return false
  el.focus({ preventScroll: true })
  return document.activeElement === el
}

/**
 * Whether focus is lost: on nothing, on `body`, or on an element no longer in
 * the page (a removed view's control; browsers move focus to `body` then).
 */
export function focusIsLost(): boolean {
  const active = document.activeElement
  return !active || active === document.body || !active.isConnected
}

/** The focused element, or null for `body` / nothing. */
export function focusedElement(): HTMLElement | null {
  const active = document.activeElement
  return active instanceof HTMLElement && active !== document.body ? active : null
}
